'use client';

import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  Component,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type CanvasPresetId = 'square' | 'portrait' | 'landscape';
type BackgroundTone = 'transparent' | 'white' | 'off-white' | 'neutral';
type WorkflowStage = 'cleanup' | 'framing';
type BrushMode = 'erase' | 'restore';

export type ProductPhotoBackgroundCleanerVariant = {
  defaultWorkflowId?: string;
  defaultCanvasPresetId?: CanvasPresetId;
  defaultCleanupBackgroundTone?: BackgroundTone;
  defaultCleanupOutputFormat?: OutputFormat;
  defaultCleanupQuality?: number;
  badgeLabel?: string;
  toolSlug?: string;
};

type CanvasPreset = {
  id: CanvasPresetId;
  label: string;
  width: number;
  height: number;
  hint: string;
  safeInset: number;
};

type WorkflowPreset = {
  id: string;
  label: string;
  summary: string;
  presetId: CanvasPresetId;
  backgroundTone: BackgroundTone;
  outputFormat: OutputFormat;
  quality: number;
};

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
  zoom: number;
  offsetX: number;
  offsetY: number;
};

type CleanupPoint = {
  x: number;
  y: number;
};

type CleanupStroke = {
  mode: BrushMode;
  size: number;
  points: CleanupPoint[];
};

type CleanupSnapshot = {
  cleanupStrokes: CleanupStroke[];
  isEdgeCleanupEnabled: boolean;
  autoCleanupSource: HTMLCanvasElement | null;
};

type BrushCursor = {
  x: number;
  y: number;
  visible: boolean;
};

type AutoCleanupPhase = 'idle' | 'preparing-model' | 'running' | 'success' | 'error' | 'cancelled';

type AutoCleanupStatus = {
  phase: AutoCleanupPhase;
  message: string;
  detail: string | null;
  acceleration: 'webgpu' | 'wasm' | null;
  isFallback: boolean;
};

type AutoCleanupWorkerResult = {
  width: number;
  height: number;
  channels: number;
  data: ArrayBuffer;
};

type AutoCleanupWorkerMessage =
  | {
      type: 'status';
      id: string;
      phase: AutoCleanupPhase;
      message: string;
      detail?: string | null;
      acceleration?: 'webgpu' | 'wasm' | null;
      isFallback?: boolean;
    }
  | {
      type: 'success';
      id: string;
      result: AutoCleanupWorkerResult;
      acceleration: 'webgpu' | 'wasm';
      isFallback: boolean;
    }
  | {
      type: 'error';
      id: string;
      message: string;
      detail?: string | null;
      acceleration?: 'webgpu' | 'wasm' | null;
      isFallback?: boolean;
    };

type StageMetrics = {
  drawX: number;
  drawY: number;
  renderedWidth: number;
  renderedHeight: number;
  scaleX: number;
  scaleY: number;
};

const canvasPresets: CanvasPreset[] = [
  {
    id: 'square',
    label: 'Square listing',
    width: 1600,
    height: 1600,
    hint: 'Best for Etsy cards, Amazon primary images, marketplace grids, and storefront product tiles.',
    safeInset: 0.1,
  },
  {
    id: 'portrait',
    label: 'Portrait listing',
    width: 1200,
    height: 1500,
    hint: 'Useful for catalog portraits, apparel, packaging, and taller product shots.',
    safeInset: 0.11,
  },
  {
    id: 'landscape',
    label: 'Landscape listing',
    width: 1600,
    height: 1200,
    hint: 'Useful for wider catalog modules, product bundles, and storefront hero-style product cards.',
    safeInset: 0.08,
  },
];

const workflowPresets: WorkflowPreset[] = [
  {
    id: 'amazon-clean',
    label: 'Amazon clean',
    summary: 'Centered square canvas with a clean white listing background.',
    presetId: 'square',
    backgroundTone: 'white',
    outputFormat: 'png',
    quality: 0.88,
  },
  {
    id: 'etsy-portrait',
    label: 'Etsy portrait',
    summary: 'Taller framing with a softer listing tone for handmade and catalog-style products.',
    presetId: 'portrait',
    backgroundTone: 'off-white',
    outputFormat: 'png',
    quality: 0.9,
  },
  {
    id: 'ebay-practical',
    label: 'eBay practical',
    summary: 'Square canvas with lighter export weight and straightforward product framing.',
    presetId: 'square',
    backgroundTone: 'neutral',
    outputFormat: 'png',
    quality: 0.78,
  },
  {
    id: 'shopify-catalog',
    label: 'Shopify catalog',
    summary: 'Balanced landscape setup for storefront cards and cleaner catalog modules.',
    presetId: 'landscape',
    backgroundTone: 'off-white',
    outputFormat: 'png',
    quality: 0.84,
  },
];

const backgroundTones: Array<{
  value: BackgroundTone;
  label: string;
  color: string;
}> = [
  { value: 'transparent', label: 'Transparent', color: 'rgba(0, 0, 0, 0)' },
  { value: 'white', label: 'White fill', color: '#ffffff' },
  { value: 'off-white', label: 'Off-white fill', color: '#f5f1e9' },
  { value: 'neutral', label: 'Neutral fill', color: '#edf1f4' },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp']);
const maxFileSize = 12 * 1024 * 1024;
const autoCleanupTimeoutMs = 45_000;
const autoCleanupIdleMessage =
  'Auto cleanup is in beta and works best on simple product shots with a clear subject.';
const autoCleanupIdleDetail =
  'Nothing is uploaded to a server. Use manual cleanup for final edge work.';

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function createRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `cleanup-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function cloneCleanupStrokes(strokes: CleanupStroke[]) {
  return strokes.map((stroke) => ({
    ...stroke,
    points: stroke.points.map((point) => ({ ...point })),
  }));
}

function supportsTransparentBackground(format: OutputFormat) {
  return format === 'png' || format === 'webp';
}

function normalizeBackgroundForFormat(tone: BackgroundTone, format: OutputFormat): BackgroundTone {
  if (tone === 'transparent' && !supportsTransparentBackground(format)) {
    return 'white';
  }

  return tone;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getContainSize(width: number, height: number, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function triggerDownload(url: string, name: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function getBackgroundConfig(tone: BackgroundTone) {
  return backgroundTones.find((option) => option.value === tone) ?? backgroundTones[0];
}

function getWorkflowById(id: string | undefined) {
  return id ? (workflowPresets.find((preset) => preset.id === id) ?? null) : null;
}

function getCanvasPresetById(id: CanvasPresetId | undefined) {
  return id ? (canvasPresets.find((preset) => preset.id === id) ?? null) : null;
}

function getPlacementMetrics(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  zoom: number,
) {
  const scaleBase = Math.min(targetWidth / imageWidth, targetHeight / imageHeight);
  const scale = scaleBase * zoom;
  const renderedWidth = imageWidth * scale;
  const renderedHeight = imageHeight * scale;
  const baseX = (targetWidth - renderedWidth) / 2;
  const baseY = (targetHeight - renderedHeight) / 2;
  const freeX = Math.max(0, targetWidth - renderedWidth);
  const freeY = Math.max(0, targetHeight - renderedHeight);
  const minX = renderedWidth > targetWidth ? targetWidth - renderedWidth : -freeX / 2;
  const maxX = renderedWidth > targetWidth ? 0 : freeX / 2;
  const minY = renderedHeight > targetHeight ? targetHeight - renderedHeight : -freeY / 2;
  const maxY = renderedHeight > targetHeight ? 0 : freeY / 2;

  return {
    renderedWidth,
    renderedHeight,
    baseX,
    baseY,
    minX,
    maxX,
    minY,
    maxY,
  };
}

function clampOffsets(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  zoom: number,
  offsetX: number,
  offsetY: number,
) {
  const metrics = getPlacementMetrics(imageWidth, imageHeight, targetWidth, targetHeight, zoom);

  return {
    x: clamp(offsetX, metrics.minX - metrics.baseX, metrics.maxX - metrics.baseX),
    y: clamp(offsetY, metrics.minY - metrics.baseY, metrics.maxY - metrics.baseY),
  };
}

function getDefaultPlacement(imageWidth: number, imageHeight: number, preset: CanvasPreset) {
  const imageAspect = imageWidth / imageHeight;
  const targetAspect = preset.width / preset.height;
  const zoom = imageAspect > targetAspect * 1.35 || imageAspect < targetAspect / 1.35 ? 0.96 : 1;

  return { zoom, offsetX: 0, offsetY: 0 };
}

function blendToward(value: number, target: number, amount: number) {
  return Math.round(value + (target - value) * amount);
}

function replaceEdgeBackground(
  context: CanvasRenderingContext2D,
  replacement: { r: number; g: number; b: number },
) {
  const { width, height } = context.canvas;
  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;
  const sampleStep = Math.max(6, Math.floor(Math.min(width, height) / 36));
  const samples: Array<{ r: number; g: number; b: number }> = [];

  const samplePixel = (x: number, y: number) => {
    const index = (y * width + x) * 4;
    const alpha = data[index + 3];

    if (alpha < 200) {
      return;
    }

    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const brightness = (r + g + b) / 3;

    if (brightness < 150) {
      return;
    }

    samples.push({ r, g, b });
  };

  for (let x = 0; x < width; x += sampleStep) {
    samplePixel(x, 0);
    samplePixel(x, height - 1);
  }

  for (let y = 0; y < height; y += sampleStep) {
    samplePixel(0, y);
    samplePixel(width - 1, y);
  }

  if (samples.length < 8) {
    return;
  }

  const reference = samples.reduce(
    (sum, sample) => ({
      r: sum.r + sample.r,
      g: sum.g + sample.g,
      b: sum.b + sample.b,
    }),
    { r: 0, g: 0, b: 0 },
  );

  reference.r /= samples.length;
  reference.g /= samples.length;
  reference.b /= samples.length;

  const tolerance = 88;
  const brightnessFloor = 120;
  const visited = new Uint8Array(width * height);
  const queue = new Uint32Array(width * height);
  let head = 0;
  let tail = 0;

  const isBackgroundLike = (index: number) => {
    const alpha = data[index + 3];

    if (alpha < 180) {
      return false;
    }

    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const brightness = (r + g + b) / 3;
    const delta = Math.abs(r - reference.r) + Math.abs(g - reference.g) + Math.abs(b - reference.b);

    return brightness >= brightnessFloor && delta <= tolerance;
  };

  const push = (x: number, y: number) => {
    const id = y * width + x;

    if (visited[id]) {
      return;
    }

    const index = id * 4;

    if (!isBackgroundLike(index)) {
      return;
    }

    visited[id] = 1;
    queue[tail] = id;
    tail += 1;
  };

  for (let x = 0; x < width; x += sampleStep) {
    push(x, 0);
    push(x, height - 1);
  }

  for (let y = 0; y < height; y += sampleStep) {
    push(0, y);
    push(width - 1, y);
  }

  while (head < tail) {
    const id = queue[head];
    head += 1;
    const x = id % width;
    const y = Math.floor(id / width);
    const index = id * 4;
    const originalR = data[index];
    const originalG = data[index + 1];
    const originalB = data[index + 2];

    data[index] = blendToward(originalR, replacement.r, 0.94);
    data[index + 1] = blendToward(originalG, replacement.g, 0.94);
    data[index + 2] = blendToward(originalB, replacement.b, 0.94);
    data[index + 3] = 255;

    if (x > 0) {
      push(x - 1, y);
    }
    if (x + 1 < width) {
      push(x + 1, y);
    }
    if (y > 0) {
      push(x, y - 1);
    }
    if (y + 1 < height) {
      push(x, y + 1);
    }
  }

  context.putImageData(imageData, 0, 0);
}

function createStrokeMask(
  width: number,
  height: number,
  stroke: CleanupStroke,
  scaleX: number,
  scaleY: number,
) {
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = width;
  maskCanvas.height = height;
  const maskContext = maskCanvas.getContext('2d');

  if (!maskContext || !stroke.points.length) {
    return null;
  }

  maskContext.save();
  maskContext.fillStyle = '#ffffff';
  maskContext.strokeStyle = '#ffffff';
  maskContext.lineCap = 'round';
  maskContext.lineJoin = 'round';
  maskContext.lineWidth = stroke.size * ((scaleX + scaleY) / 2);

  if (stroke.points.length === 1) {
    const point = stroke.points[0];
    maskContext.beginPath();
    maskContext.ellipse(
      point.x * scaleX,
      point.y * scaleY,
      Math.max(1, (stroke.size * scaleX) / 2),
      Math.max(1, (stroke.size * scaleY) / 2),
      0,
      0,
      Math.PI * 2,
    );
    maskContext.fill();
    maskContext.restore();
    return maskCanvas;
  }

  maskContext.beginPath();
  maskContext.moveTo(stroke.points[0].x * scaleX, stroke.points[0].y * scaleY);

  for (let index = 1; index < stroke.points.length; index += 1) {
    const point = stroke.points[index];
    maskContext.lineTo(point.x * scaleX, point.y * scaleY);
  }

  maskContext.stroke();
  maskContext.restore();
  return maskCanvas;
}

function createStrokeLayer(width: number, height: number) {
  const layerCanvas = document.createElement('canvas');
  layerCanvas.width = width;
  layerCanvas.height = height;
  const layerContext = layerCanvas.getContext('2d');

  if (!layerContext) {
    return null;
  }

  return { layerCanvas, layerContext };
}

function buildCanvasFromRgbaBuffer(result: AutoCleanupWorkerResult) {
  const expectedLength = result.width * result.height * 4;

  if (
    result.width <= 0 ||
    result.height <= 0 ||
    result.channels !== 4 ||
    result.data.byteLength !== expectedLength
  ) {
    return null;
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = result.width;
    canvas.height = result.height;
    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    const imageData = new ImageData(
      new Uint8ClampedArray(result.data),
      result.width,
      result.height,
    );
    context.putImageData(imageData, 0, 0);
    return canvas;
  } catch {
    return null;
  }
}

type ToolRenderErrorBoundaryProps = {
  children: ReactNode;
};

type ToolRenderErrorBoundaryState = {
  hasError: boolean;
};

class ToolRenderErrorBoundary extends Component<
  ToolRenderErrorBoundaryProps,
  ToolRenderErrorBoundaryState
> {
  constructor(props: ToolRenderErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {}

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <article className="tool-live-card">
        <div className="tool-live-header">
          <div>
            <h2>Marketplace background cleanup and framing</h2>
            <p>
              The editor hit a local rendering issue, but the page stayed available. Reload the tool
              to continue, or upload the product image again if needed.
            </p>
          </div>
          <span className="tool-live-badge">Local browser processing</span>
        </div>
        <div className="tool-marketplace-cleaner-auto-note">
          <strong>Something interrupted the local editor.</strong>
          <span>
            This tool keeps processing in the browser only. Reloading the page is the safest way to
            recover from a render error without leaving the tools section.
          </span>
          <div className="tool-marketplace-cleaner-auto-actions">
            <button className="button" onClick={() => window.location.reload()} type="button">
              Reload tool
            </button>
          </div>
        </div>
      </article>
    );
  }
}

function drawPreparedCanvas(
  context: CanvasRenderingContext2D,
  image: LoadedImage,
  autoCleanupSource: HTMLCanvasElement | null,
  presetWidth: number,
  presetHeight: number,
  referenceWidth: number,
  referenceHeight: number,
  backgroundTone: BackgroundTone,
  cleanupStrokes: CleanupStroke[],
  isEdgeCleanupEnabled: boolean,
) {
  const background = getBackgroundConfig(backgroundTone);
  const primarySource = autoCleanupSource ?? image.element;
  context.clearRect(0, 0, presetWidth, presetHeight);

  if (backgroundTone !== 'transparent') {
    context.save();
    context.fillStyle = background.color;
    context.fillRect(0, 0, presetWidth, presetHeight);
    context.restore();
  }

  const scaledOffsetX = image.offsetX * (presetWidth / referenceWidth);
  const scaledOffsetY = image.offsetY * (presetHeight / referenceHeight);
  const metrics = getPlacementMetrics(
    image.width,
    image.height,
    presetWidth,
    presetHeight,
    image.zoom,
  );
  const clampedOffsets = clampOffsets(
    image.width,
    image.height,
    presetWidth,
    presetHeight,
    image.zoom,
    scaledOffsetX,
    scaledOffsetY,
  );
  const drawX = metrics.baseX + clampedOffsets.x;
  const drawY = metrics.baseY + clampedOffsets.y;
  const stageMetrics: StageMetrics = {
    drawX,
    drawY,
    renderedWidth: metrics.renderedWidth,
    renderedHeight: metrics.renderedHeight,
    scaleX: presetWidth / referenceWidth,
    scaleY: presetHeight / referenceHeight,
  };

  context.drawImage(primarySource, drawX, drawY, metrics.renderedWidth, metrics.renderedHeight);

  if (isEdgeCleanupEnabled && backgroundTone !== 'transparent') {
    replaceEdgeBackground(
      context,
      backgroundTone === 'white'
        ? { r: 255, g: 255, b: 255 }
        : backgroundTone === 'off-white'
          ? { r: 245, g: 241, b: 233 }
          : { r: 237, g: 241, b: 244 },
    );
  }

  cleanupStrokes.forEach((stroke) => {
    const mask = createStrokeMask(
      presetWidth,
      presetHeight,
      stroke,
      stageMetrics.scaleX,
      stageMetrics.scaleY,
    );

    if (!mask) {
      return;
    }

    const layer = createStrokeLayer(presetWidth, presetHeight);

    if (!layer) {
      return;
    }

    if (stroke.mode === 'erase') {
      if (backgroundTone === 'transparent') {
        context.save();
        context.globalCompositeOperation = 'destination-out';
        context.drawImage(mask, 0, 0);
        context.restore();
        return;
      }

      layer.layerContext.save();
      layer.layerContext.fillStyle = background.color;
      layer.layerContext.fillRect(0, 0, presetWidth, presetHeight);
      layer.layerContext.globalCompositeOperation = 'destination-in';
      layer.layerContext.drawImage(mask, 0, 0);
      layer.layerContext.restore();
      context.drawImage(layer.layerCanvas, 0, 0);
      return;
    }

    layer.layerContext.save();
    layer.layerContext.drawImage(
      image.element,
      stageMetrics.drawX,
      stageMetrics.drawY,
      stageMetrics.renderedWidth,
      stageMetrics.renderedHeight,
    );
    layer.layerContext.globalCompositeOperation = 'destination-in';
    layer.layerContext.drawImage(mask, 0, 0);
    layer.layerContext.restore();
    context.drawImage(layer.layerCanvas, 0, 0);
  });
}

function getEstimatedOutputWeight(
  image: LoadedImage | null,
  format: OutputFormat,
  quality: number,
) {
  if (!image) {
    return 'Will appear after upload';
  }

  if (format === 'png') {
    return `Around ${formatFileSize(Math.max(90_000, Math.round(image.size * 0.95)))}`;
  }

  const ratio = format === 'webp' ? quality * 0.44 : quality * 0.6;
  return `Around ${formatFileSize(Math.max(45_000, Math.round(image.size * ratio)))}`;
}

type BrowserProductPhotoBackgroundCleanerProps = {
  variant?: ProductPhotoBackgroundCleanerVariant;
};

function BrowserProductPhotoBackgroundCleanerInner({
  variant,
}: BrowserProductPhotoBackgroundCleanerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previewFrameRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceFileRef = useRef<File | null>(null);
  const autoCleanupWorkerRef = useRef<Worker | null>(null);
  const autoCleanupRequestIdRef = useRef<string | null>(null);
  const autoCleanupTimeoutRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const cleanupStrokesRef = useRef<CleanupStroke[]>([]);
  const isEdgeCleanupEnabledRef = useRef(false);
  const autoCleanupSourceRef = useRef<HTMLCanvasElement | null>(null);
  const dragPointerRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const brushPointerRef = useRef<{
    pointerId: number;
    strokeIndex: number;
  } | null>(null);

  const configuredWorkflow = getWorkflowById(variant?.defaultWorkflowId);
  const initialOutputFormat =
    variant?.defaultCleanupOutputFormat ?? configuredWorkflow?.outputFormat ?? 'png';
  const defaultPreset =
    getCanvasPresetById(
      variant?.defaultCanvasPresetId ?? configuredWorkflow?.presetId ?? workflowPresets[0].presetId,
    ) ?? canvasPresets[0];
  const initialBackgroundTone = normalizeBackgroundForFormat(
    variant?.defaultCleanupBackgroundTone ??
      configuredWorkflow?.backgroundTone ??
      workflowPresets[0].backgroundTone,
    initialOutputFormat,
  );
  const initialQuality =
    variant?.defaultCleanupQuality ?? configuredWorkflow?.quality ?? workflowPresets[0].quality;
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [activeWorkflowId, setActiveWorkflowId] = useState(configuredWorkflow?.id ?? '');
  const [activePresetId, setActivePresetId] = useState<CanvasPresetId>(defaultPreset.id);
  const [backgroundTone, setBackgroundTone] = useState<BackgroundTone>(initialBackgroundTone);
  const [workflowStage, setWorkflowStage] = useState<WorkflowStage>('cleanup');
  const [brushMode, setBrushMode] = useState<BrushMode>('erase');
  const [brushSize, setBrushSize] = useState(42);
  const [isEdgeCleanupEnabled, setIsEdgeCleanupEnabled] = useState(false);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(initialOutputFormat);
  const [quality, setQuality] = useState(initialQuality);
  const [cleanupStrokes, setCleanupStrokes] = useState<CleanupStroke[]>([]);
  const [cleanupHistory, setCleanupHistory] = useState<CleanupSnapshot[]>([]);
  const [autoCleanupSource, setAutoCleanupSource] = useState<HTMLCanvasElement | null>(null);
  const [autoCleanupStatus, setAutoCleanupStatus] = useState<AutoCleanupStatus>({
    phase: 'idle',
    message: autoCleanupIdleMessage,
    detail: autoCleanupIdleDetail,
    acceleration: null,
    isFallback: false,
  });
  const [brushCursor, setBrushCursor] = useState<BrushCursor>({
    x: 0,
    y: 0,
    visible: false,
  });
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPointerDragging, setIsPointerDragging] = useState(false);
  const [isPainting, setIsPainting] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [previewFrameSize, setPreviewFrameSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const activePreset = useMemo(
    () => canvasPresets.find((preset) => preset.id === activePresetId) ?? canvasPresets[0],
    [activePresetId],
  );
  const activeWorkflow = useMemo(
    () => workflowPresets.find((preset) => preset.id === activeWorkflowId) ?? null,
    [activeWorkflowId],
  );
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const activeBackground = useMemo(() => getBackgroundConfig(backgroundTone), [backgroundTone]);
  const previewSize = useMemo(
    () =>
      getContainSize(
        activePreset.width,
        activePreset.height,
        Math.max(320, (previewFrameSize?.width ?? 1080) - 40),
        Math.max(260, (previewFrameSize?.height ?? 720) - 40),
      ),
    [activePreset.height, activePreset.width, previewFrameSize],
  );
  const previewScaleX = previewSize.width / activePreset.width;
  const previewScaleY = previewSize.height / activePreset.height;
  const safeInsetLabel = `${Math.round(activePreset.safeInset * 100)}%`;
  const estimatedOutputWeight = useMemo(
    () => getEstimatedOutputWeight(loadedImage, outputFormat, quality),
    [loadedImage, outputFormat, quality],
  );
  const isAutoCleanupBusy =
    autoCleanupStatus.phase === 'preparing-model' || autoCleanupStatus.phase === 'running';
  const canUseTransparentBackground = supportsTransparentBackground(outputFormat);
  const activeManualLabel = brushMode === 'restore' ? 'Restore mode' : 'Erase mode';

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (autoCleanupTimeoutRef.current) {
        window.clearTimeout(autoCleanupTimeoutRef.current);
        autoCleanupTimeoutRef.current = null;
      }
      autoCleanupWorkerRef.current?.terminate();
      autoCleanupWorkerRef.current = null;
      autoCleanupRequestIdRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!previewFrameRef.current) {
      return;
    }

    const frame = previewFrameRef.current;
    const syncFrameSize = () => {
      setPreviewFrameSize({
        width: frame.clientWidth,
        height: frame.clientHeight,
      });
    };

    syncFrameSize();
    const observer = new ResizeObserver(syncFrameSize);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    cleanupStrokesRef.current = cleanupStrokes;
  }, [cleanupStrokes]);

  useEffect(() => {
    isEdgeCleanupEnabledRef.current = isEdgeCleanupEnabled;
  }, [isEdgeCleanupEnabled]);

  useEffect(() => {
    autoCleanupSourceRef.current = autoCleanupSource;
  }, [autoCleanupSource]);

  useEffect(() => {
    if (!loadedImage || !previewCanvasRef.current) {
      return;
    }

    const canvas = previewCanvasRef.current;
    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.round(previewSize.width * devicePixelRatio);
    canvas.height = Math.round(previewSize.height * devicePixelRatio);

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(devicePixelRatio, devicePixelRatio);
    drawPreparedCanvas(
      context,
      loadedImage,
      autoCleanupSource,
      previewSize.width,
      previewSize.height,
      activePreset.width,
      activePreset.height,
      backgroundTone,
      cleanupStrokes,
      isEdgeCleanupEnabled,
    );
  }, [
    activePreset.height,
    activePreset.width,
    backgroundTone,
    cleanupStrokes,
    autoCleanupSource,
    isEdgeCleanupEnabled,
    loadedImage,
    previewSize.height,
    previewSize.width,
  ]);

  useEffect(() => {
    setBrushCursor((current) => ({ ...current, visible: false }));
  }, [activePreset.id, workflowStage]);

  function resetOutputState() {
    setResultSize(null);
    setCreditMessage(null);
  }

  function resetCleanupState() {
    setCleanupStrokes([]);
    setIsEdgeCleanupEnabled(false);
    setCleanupHistory([]);
    resetOutputState();
  }

  function clearAutoCleanupTimeout() {
    if (autoCleanupTimeoutRef.current) {
      window.clearTimeout(autoCleanupTimeoutRef.current);
      autoCleanupTimeoutRef.current = null;
    }
  }

  function terminateAutoCleanupWorker() {
    autoCleanupWorkerRef.current?.terminate();
    autoCleanupWorkerRef.current = null;
  }

  function setAutoCleanupStatusSafe(nextStatus: AutoCleanupStatus) {
    if (!isMountedRef.current) {
      return;
    }

    setAutoCleanupStatus(nextStatus);
  }

  function failAutoCleanup(
    message: string,
    detail: string,
    options?: {
      acceleration?: 'webgpu' | 'wasm' | null;
      isFallback?: boolean;
      retainSource?: boolean;
      terminateWorker?: boolean;
    },
  ) {
    clearAutoCleanupTimeout();
    autoCleanupRequestIdRef.current = null;

    if (!options?.retainSource) {
      setAutoCleanupSource(null);
    }

    if (options?.terminateWorker ?? true) {
      terminateAutoCleanupWorker();
    }

    setAutoCleanupStatusSafe({
      phase: 'error',
      message,
      detail,
      acceleration: options?.acceleration ?? null,
      isFallback: options?.isFallback ?? false,
    });
  }

  function resetAutoCleanupState() {
    clearAutoCleanupTimeout();
    autoCleanupRequestIdRef.current = null;
    terminateAutoCleanupWorker();
    setAutoCleanupSource(null);
    setCleanupHistory([]);
    setAutoCleanupStatusSafe({
      phase: 'idle',
      message: autoCleanupIdleMessage,
      detail: autoCleanupIdleDetail,
      acceleration: null,
      isFallback: false,
    });
  }

  function markWorkflowCustom() {
    setActiveWorkflowId('');
  }

  function pushCleanupHistorySnapshot() {
    setCleanupHistory((current) => [
      ...current.slice(-23),
      {
        cleanupStrokes: cloneCleanupStrokes(cleanupStrokesRef.current),
        isEdgeCleanupEnabled: isEdgeCleanupEnabledRef.current,
        autoCleanupSource: autoCleanupSourceRef.current,
      },
    ]);
  }

  function undoCleanupChange() {
    setCleanupHistory((current) => {
      const snapshot = current[current.length - 1];

      if (!snapshot) {
        return current;
      }

      setCleanupStrokes(cloneCleanupStrokes(snapshot.cleanupStrokes));
      setIsEdgeCleanupEnabled(snapshot.isEdgeCleanupEnabled);
      setAutoCleanupSource(snapshot.autoCleanupSource);
      resetOutputState();
      return current.slice(0, -1);
    });
  }

  function applyWorkflowPreset(workflow: WorkflowPreset) {
    setActiveWorkflowId(workflow.id);
    setActivePresetId(workflow.presetId);
    setOutputFormat(workflow.outputFormat);
    setBackgroundTone(normalizeBackgroundForFormat(workflow.backgroundTone, workflow.outputFormat));
    setQuality(workflow.quality);
    resetCleanupState();

    if (!loadedImage) {
      return;
    }

    const nextPreset =
      canvasPresets.find((preset) => preset.id === workflow.presetId) ?? activePreset;
    const placement = getDefaultPlacement(loadedImage.width, loadedImage.height, nextPreset);
    setLoadedImage((current) =>
      current
        ? {
            ...current,
            zoom: placement.zoom,
            offsetX: placement.offsetX,
            offsetY: placement.offsetY,
          }
        : current,
    );
  }

  function centerProduct() {
    if (!loadedImage) {
      return;
    }

    setLoadedImage((current) =>
      current
        ? {
            ...current,
            offsetX: 0,
            offsetY: 0,
          }
        : current,
    );
    resetOutputState();
  }

  function autoFrame() {
    if (!loadedImage) {
      return;
    }

    const placement = getDefaultPlacement(loadedImage.width, loadedImage.height, activePreset);
    setLoadedImage((current) =>
      current
        ? {
            ...current,
            zoom: placement.zoom,
            offsetX: placement.offsetX,
            offsetY: placement.offsetY,
          }
        : current,
    );
    resetOutputState();
  }

  function switchToManualCleanup(mode: BrushMode = 'erase') {
    setWorkflowStage('cleanup');
    setBrushMode(mode);
  }

  function updateZoom(nextZoom: number) {
    if (!loadedImage) {
      return;
    }

    markWorkflowCustom();
    const clampedZoom = clamp(nextZoom, 0.72, 2.6);
    const nextOffsets = clampOffsets(
      loadedImage.width,
      loadedImage.height,
      activePreset.width,
      activePreset.height,
      clampedZoom,
      loadedImage.offsetX,
      loadedImage.offsetY,
    );

    setLoadedImage((current) =>
      current
        ? {
            ...current,
            zoom: clampedZoom,
            offsetX: nextOffsets.x,
            offsetY: nextOffsets.y,
          }
        : current,
    );
    resetOutputState();
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setCleanupStrokes([]);
    setIsEdgeCleanupEnabled(false);
    setWorkflowStage('cleanup');
    setBrushMode('erase');
    sourceFileRef.current = file;
    resetAutoCleanupState();

    if (!file) {
      setLoadedImage(null);
      return;
    }

    if (!supportedTypes.has(file.type) || file.size > maxFileSize) {
      setErrorMessage('Choose one PNG, JPG, JPEG, WebP, GIF, or BMP image up to 12 MB.');
      return;
    }

    setIsLoadingImage(true);

    try {
      const image = await loadImageFromFile(file);
      const placement = getDefaultPlacement(image.naturalWidth, image.naturalHeight, activePreset);

      if (!isMountedRef.current || sourceFileRef.current !== file) {
        return;
      }

      setLoadedImage({
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
        zoom: placement.zoom,
        offsetX: placement.offsetX,
        offsetY: placement.offsetY,
      });
    } catch (error) {
      if (!isMountedRef.current || sourceFileRef.current !== file) {
        return;
      }

      setLoadedImage(null);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'This product photo could not be prepared in your browser.',
      );
    } finally {
      if (isMountedRef.current && sourceFileRef.current === file) {
        setIsLoadingImage(false);
      }
    }
  }

  function ensureAutoCleanupWorker() {
    if (autoCleanupWorkerRef.current) {
      return autoCleanupWorkerRef.current;
    }

    let worker: Worker;

    try {
      worker = new Worker(new URL('./background-removal.worker.ts', import.meta.url), {
        type: 'module',
      });
    } catch {
      failAutoCleanup(
        'Auto cleanup is unavailable in this browser right now.',
        'You can continue with manual cleanup and framing without losing the uploaded image.',
        { retainSource: true, terminateWorker: true },
      );
      return null;
    }

    worker.onmessage = (event: MessageEvent<AutoCleanupWorkerMessage>) => {
      try {
        const payload = event.data;

        if (!payload || payload.id !== autoCleanupRequestIdRef.current) {
          return;
        }

        if (payload.type === 'status') {
          setAutoCleanupStatusSafe({
            phase: payload.phase,
            message: payload.message,
            detail: payload.detail ?? null,
            acceleration: payload.acceleration ?? null,
            isFallback: payload.isFallback ?? false,
          });
          return;
        }

        clearAutoCleanupTimeout();

        if (payload.type === 'success') {
          const nextCanvas = buildCanvasFromRgbaBuffer(payload.result);

          if (!nextCanvas) {
            failAutoCleanup(
              'Auto cleanup returned an invalid local mask.',
              'Continue with manual cleanup, or retry auto cleanup on this photo.',
              {
                acceleration: payload.acceleration,
                isFallback: payload.isFallback,
                retainSource: true,
                terminateWorker: false,
              },
            );
            return;
          }

          autoCleanupRequestIdRef.current = null;
          pushCleanupHistorySnapshot();
          setAutoCleanupSource(nextCanvas);
          setAutoCleanupStatusSafe({
            phase: 'success',
            message: 'Auto cleanup created a starter mask.',
            detail: 'Refine edges with Manual erase, Restore area, or Cleanup edges if needed.',
            acceleration: payload.acceleration,
            isFallback: payload.isFallback,
          });
          return;
        }

        failAutoCleanup(
          payload.message,
          payload.detail ??
            'Auto cleanup is optional. Manual cleanup and export are still available.',
          {
            acceleration: payload.acceleration ?? null,
            isFallback: payload.isFallback ?? false,
            retainSource: true,
            terminateWorker: false,
          },
        );
      } catch {
        failAutoCleanup(
          'Auto cleanup hit a local processing error.',
          'You can retry it, or continue with manual cleanup without losing the current photo.',
          { retainSource: true, terminateWorker: true },
        );
      }
    };

    worker.onerror = (event) => {
      event.preventDefault();
      failAutoCleanup(
        'Auto cleanup could not start on this device.',
        'Continue with manual cleanup, or retry auto cleanup after reloading the tool.',
        { retainSource: true, terminateWorker: true },
      );
    };

    worker.onmessageerror = () => {
      failAutoCleanup(
        'Auto cleanup returned unreadable data in this browser.',
        'Manual cleanup stays available, and you can retry auto cleanup if needed.',
        { retainSource: true, terminateWorker: true },
      );
    };

    autoCleanupWorkerRef.current = worker;
    return worker;
  }

  function cancelAutoCleanup() {
    clearAutoCleanupTimeout();
    autoCleanupRequestIdRef.current = null;
    terminateAutoCleanupWorker();
    setAutoCleanupStatusSafe({
      phase: 'cancelled',
      message: 'Auto cleanup was cancelled.',
      detail: 'You can retry it, or keep working with the manual cleanup tools.',
      acceleration: null,
      isFallback: false,
    });
  }

  function runAutoCleanup() {
    if (isAutoCleanupBusy) {
      return;
    }

    if (!loadedImage || !sourceFileRef.current) {
      setAutoCleanupStatusSafe({
        phase: 'error',
        message: 'Upload one product photo before using auto cleanup.',
        detail: 'Manual cleanup stays available as the main path.',
        acceleration: null,
        isFallback: false,
      });
      return;
    }

    const requestId = createRequestId();
    autoCleanupRequestIdRef.current = requestId;
    const worker = ensureAutoCleanupWorker();

    if (!worker) {
      return;
    }

    clearAutoCleanupTimeout();
    setWorkflowStage('cleanup');
    setAutoCleanupStatusSafe({
      phase: 'preparing-model',
      message: 'Preparing model locally in your browser…',
      detail: 'Using device acceleration when available. Nothing is uploaded to a server.',
      acceleration: null,
      isFallback: false,
    });

    autoCleanupTimeoutRef.current = window.setTimeout(() => {
      if (autoCleanupRequestIdRef.current !== requestId) {
        return;
      }

      failAutoCleanup(
        'Auto cleanup took too long on this device.',
        'You can retry it, or continue with manual cleanup and framing.',
        { retainSource: true, terminateWorker: true },
      );
    }, autoCleanupTimeoutMs);

    try {
      worker.postMessage({
        type: 'run',
        id: requestId,
        file: sourceFileRef.current,
        preferWebGpu: true,
      });
    } catch {
      failAutoCleanup(
        'Auto cleanup could not start in this browser.',
        'Manual cleanup remains fully available for this product photo.',
        { retainSource: true, terminateWorker: true },
      );
    }
  }

  function getStageRect() {
    return stageRef.current?.getBoundingClientRect() ?? null;
  }

  function getCanvasPointFromClient(clientX: number, clientY: number) {
    const rect = getStageRect();

    if (!rect || rect.width <= 0 || rect.height <= 0) {
      return null;
    }

    const localX = clamp(clientX - rect.left, 0, rect.width);
    const localY = clamp(clientY - rect.top, 0, rect.height);

    return {
      x: localX * (activePreset.width / rect.width),
      y: localY * (activePreset.height / rect.height),
    };
  }

  function updateBrushCursorFromClient(
    clientX: number,
    clientY: number,
    pointerType: string,
    visible = true,
  ) {
    const point = getCanvasPointFromClient(clientX, clientY);

    if (!point) {
      setBrushCursor((current) => ({ ...current, visible: false }));
      return;
    }

    setBrushCursor({
      x: point.x,
      y: point.y,
      visible: pointerType !== 'touch' ? visible : isPainting || visible,
    });
  }

  function handlePointerEnter(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!loadedImage || workflowStage !== 'cleanup') {
      return;
    }

    updateBrushCursorFromClient(event.clientX, event.clientY, event.pointerType);
  }

  function handlePointerLeave(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
      setBrushCursor((current) => ({ ...current, visible: false }));
    }
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!loadedImage) {
      return;
    }

    if (workflowStage === 'cleanup') {
      const point = getCanvasPointFromClient(event.clientX, event.clientY);

      if (!point) {
        return;
      }

      pushCleanupHistorySnapshot();
      const nextStroke: CleanupStroke = {
        mode: brushMode,
        size: brushSize,
        points: [point],
      };

      setCleanupStrokes((current) => {
        brushPointerRef.current = {
          pointerId: event.pointerId,
          strokeIndex: current.length,
        };
        return [...current, nextStroke];
      });
      setIsPainting(true);
      updateBrushCursorFromClient(event.clientX, event.clientY, event.pointerType);
      resetOutputState();
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    dragPointerRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: loadedImage.offsetX,
      offsetY: loadedImage.offsetY,
    };
    setIsPointerDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!loadedImage) {
      return;
    }

    if (workflowStage === 'cleanup') {
      updateBrushCursorFromClient(event.clientX, event.clientY, event.pointerType);
    }

    if (
      workflowStage === 'cleanup' &&
      brushPointerRef.current &&
      brushPointerRef.current.pointerId === event.pointerId
    ) {
      const point = getCanvasPointFromClient(event.clientX, event.clientY);

      if (!point) {
        return;
      }

      setCleanupStrokes((current) => {
        const nextStrokes = [...current];
        const stroke = nextStrokes[brushPointerRef.current!.strokeIndex];

        if (!stroke) {
          return current;
        }

        const lastPoint = stroke.points[stroke.points.length - 1];
        const deltaX = point.x - lastPoint.x;
        const deltaY = point.y - lastPoint.y;

        if (deltaX * deltaX + deltaY * deltaY < 6) {
          return current;
        }

        nextStrokes[brushPointerRef.current!.strokeIndex] = {
          ...stroke,
          points: [...stroke.points, point],
        };
        return nextStrokes;
      });

      return;
    }

    if (!dragPointerRef.current || dragPointerRef.current.pointerId !== event.pointerId) {
      return;
    }

    const rect = getStageRect();

    if (!rect) {
      return;
    }

    const scaleX = activePreset.width / rect.width;
    const scaleY = activePreset.height / rect.height;
    const nextOffsets = clampOffsets(
      loadedImage.width,
      loadedImage.height,
      activePreset.width,
      activePreset.height,
      loadedImage.zoom,
      dragPointerRef.current.offsetX + (event.clientX - dragPointerRef.current.startX) * scaleX,
      dragPointerRef.current.offsetY + (event.clientY - dragPointerRef.current.startY) * scaleY,
    );

    setLoadedImage((current) =>
      current
        ? {
            ...current,
            offsetX: nextOffsets.x,
            offsetY: nextOffsets.y,
          }
        : current,
    );
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (brushPointerRef.current?.pointerId === event.pointerId) {
      brushPointerRef.current = null;
      setIsPainting(false);
      if (event.pointerType === 'touch') {
        setBrushCursor((current) => ({ ...current, visible: false }));
      }
    }

    if (dragPointerRef.current?.pointerId === event.pointerId) {
      dragPointerRef.current = null;
      setIsPointerDragging(false);
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  async function runExport() {
    if (!loadedImage) {
      setErrorMessage('Upload one product image first.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: variant?.toolSlug ?? 'product-photo-background-cleaner' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const canvas = document.createElement('canvas');
      canvas.width = activePreset.width;
      canvas.height = activePreset.height;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare the export.');
      }

      drawPreparedCanvas(
        context,
        loadedImage,
        autoCleanupSource,
        activePreset.width,
        activePreset.height,
        activePreset.width,
        activePreset.height,
        backgroundTone,
        cleanupStrokes,
        isEdgeCleanupEnabled,
      );

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The marketplace-ready export could not finish for this image.');
      }

      const fileName = `${getBaseName(loadedImage.name)}-${activePreset.id}-clean.${selectedOutput.extension}`;
      const downloadUrl = URL.createObjectURL(blob);
      triggerDownload(downloadUrl, fileName);
      setResultSize(blob.size);
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 4000);

      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Local export finished. Metadata stripped on save.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Local export finished. Metadata stripped on save. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Local export finished. Metadata stripped on save.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The product photo export could not finish.',
      );
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Clean up the background, center the product, export the listing image</h2>
          <p>
            Upload one product photo, clean the background, frame it, and export a marketplace-ready
            file. Everything stays local in the browser.
          </p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Local browser processing'}</span>
      </div>

      <div className="tool-product-prep-steps" aria-label="Workflow steps">
        <div className="tool-product-prep-step">
          <strong>1. Upload</strong>
          <span>Add one product photo</span>
        </div>
        <div className="tool-product-prep-step">
          <strong>2. Prepare</strong>
          <span>Cleanup, framing</span>
        </div>
        <div className="tool-product-prep-step">
          <strong>3. Export</strong>
          <span>Download a listing-ready image</span>
        </div>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Upload</strong>
              <span>Use one product shot and keep the whole flow local.</span>
            </div>
            <button
              className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
              onClick={() => inputRef.current?.click()}
              onDragEnter={() => setIsDragActive(true)}
              onDragLeave={() => setIsDragActive(false)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragActive(false);
                void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
              }}
              type="button"
            >
              <div>
                <strong>
                  {isLoadingImage
                    ? 'Preparing product photo locally…'
                    : loadedImage
                      ? loadedImage.name
                      : 'Upload product photo or drop it here'}
                </strong>
                <span>
                  {loadedImage
                    ? `${formatFileSize(loadedImage.size)} • ${normalizeSourceLabel(loadedImage.type)} • ${loadedImage.width} × ${loadedImage.height}`
                    : 'PNG, JPG, JPEG, WebP, GIF, or BMP up to 12 MB. Local cleanup and framing only.'}
                </span>
              </div>
            </button>
            <input
              ref={inputRef}
              accept="image/*"
              className="tool-live-file-input"
              onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
              type="file"
            />
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Prepare</strong>
              <span>Pick a listing setup, clean it up, then fine-tune the frame.</span>
            </div>

            <div className="tool-live-routes" aria-label="Marketplace quick setups">
              {workflowPresets.map((workflow) => (
                <button
                  key={workflow.id}
                  className={`tool-live-route ${activeWorkflowId === workflow.id ? 'tool-live-route-active' : ''}`}
                  onClick={() => applyWorkflowPreset(workflow)}
                  type="button"
                >
                  {workflow.label}
                </button>
              ))}
            </div>
            <p className="tool-product-prep-preset-note">
              {activeWorkflow?.summary ?? activePreset.hint}
            </p>

            <div className="tool-marketplace-cleaner-stages" aria-label="Editing stages">
              <button
                className={`tool-marketplace-cleaner-stage-tab ${
                  workflowStage === 'cleanup' ? 'tool-marketplace-cleaner-stage-tab-active' : ''
                }`}
                onClick={() => setWorkflowStage('cleanup')}
                type="button"
              >
                Background cleanup
              </button>
              <button
                className={`tool-marketplace-cleaner-stage-tab ${
                  workflowStage === 'framing' ? 'tool-marketplace-cleaner-stage-tab-active' : ''
                }`}
                onClick={() => setWorkflowStage('framing')}
                type="button"
              >
                Framing
              </button>
            </div>

            <div className="tool-live-preview tool-editor-preview tool-marketplace-cleaner-shell">
              <div className="tool-image-editor-header">
                <h3>{workflowStage === 'cleanup' ? 'Cleanup' : 'Framing'}</h3>
                {workflowStage === 'framing' ? (
                  <div className="tool-image-editor-header-actions tool-marketplace-cleaner-editor-actions">
                    <button
                      className="button"
                      disabled={!loadedImage}
                      onClick={autoFrame}
                      type="button"
                    >
                      Auto frame
                    </button>
                    <button
                      className="button button-ghost"
                      disabled={!loadedImage}
                      onClick={centerProduct}
                      type="button"
                    >
                      Center product
                    </button>
                  </div>
                ) : null}
              </div>
              {workflowStage === 'cleanup' ? (
                <div className="tool-marketplace-cleaner-action-groups">
                  <div className="tool-marketplace-cleaner-action-card">
                    <div className="tool-marketplace-cleaner-action-copy">
                      <strong>Quick start</strong>
                      <span>Try Auto cleanup first, or go straight to the manual brush.</span>
                    </div>
                    <div className="tool-marketplace-cleaner-primary-actions">
                      <button
                        className={`button ${
                          autoCleanupStatus.phase === 'success' ? 'button-ghost' : ''
                        }`}
                        disabled={!loadedImage || isAutoCleanupBusy}
                        onClick={runAutoCleanup}
                        type="button"
                      >
                        {isAutoCleanupBusy ? 'Running auto cleanup…' : 'Auto cleanup (Beta)'}
                      </button>
                      <button
                        className="button button-ghost"
                        disabled={!loadedImage}
                        onClick={() => switchToManualCleanup('erase')}
                        type="button"
                      >
                        Manual cleanup
                      </button>
                      {isAutoCleanupBusy ? (
                        <button
                          className="button button-ghost"
                          onClick={cancelAutoCleanup}
                          type="button"
                        >
                          Cancel
                        </button>
                      ) : null}
                      {autoCleanupStatus.phase === 'error' ||
                      autoCleanupStatus.phase === 'cancelled' ? (
                        <button
                          className="button button-ghost"
                          disabled={!loadedImage}
                          onClick={runAutoCleanup}
                          type="button"
                        >
                          Retry
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="tool-marketplace-cleaner-action-card tool-marketplace-cleaner-action-card-secondary">
                    <div className="tool-marketplace-cleaner-action-copy">
                      <strong>Manual tools</strong>
                      <span>{activeManualLabel}</span>
                    </div>
                    <div className="tool-marketplace-cleaner-secondary-actions">
                      <button
                        className={`button button-ghost ${
                          brushMode === 'erase' ? 'tool-marketplace-cleaner-mode-active' : ''
                        }`}
                        disabled={!loadedImage}
                        onClick={() => setBrushMode('erase')}
                        type="button"
                      >
                        Erase
                      </button>
                      <button
                        className={`button button-ghost ${
                          brushMode === 'restore' ? 'tool-marketplace-cleaner-mode-active' : ''
                        }`}
                        disabled={!loadedImage}
                        onClick={() => setBrushMode('restore')}
                        type="button"
                      >
                        Restore
                      </button>
                      <button
                        className={`button button-ghost ${
                          isEdgeCleanupEnabled ? 'tool-marketplace-cleaner-mode-active' : ''
                        }`}
                        disabled={!loadedImage}
                        onClick={() => {
                          pushCleanupHistorySnapshot();
                          setIsEdgeCleanupEnabled((current) => !current);
                          resetOutputState();
                        }}
                        type="button"
                      >
                        Edges
                      </button>
                      <button
                        className="button button-ghost"
                        disabled={!cleanupHistory.length}
                        onClick={undoCleanupChange}
                        type="button"
                      >
                        Undo
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
              <div
                ref={previewFrameRef}
                className="tool-live-preview-frame tool-editor-preview-frame tool-product-prep-frame"
              >
                {loadedImage ? (
                  <div
                    ref={stageRef}
                    className="tool-marketplace-cleaner-stage"
                    aria-label={`${activePreset.label} preview`}
                    style={{
                      aspectRatio: `${previewSize.width} / ${previewSize.height}`,
                      width: `${previewSize.width}px`,
                      height: `${previewSize.height}px`,
                    }}
                  >
                    <canvas
                      ref={previewCanvasRef}
                      className={`tool-editor-canvas tool-marketplace-cleaner-canvas ${
                        workflowStage === 'cleanup'
                          ? isPainting
                            ? 'tool-marketplace-cleaner-canvas-painting'
                            : 'tool-marketplace-cleaner-canvas-cleanup'
                          : isPointerDragging
                            ? 'tool-marketplace-cleaner-canvas-dragging'
                            : ''
                      }`}
                      onPointerCancel={handlePointerUp}
                      onPointerDown={handlePointerDown}
                      onPointerEnter={handlePointerEnter}
                      onPointerLeave={handlePointerLeave}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                    />
                    {workflowStage === 'cleanup' && brushCursor.visible ? (
                      <div
                        aria-hidden="true"
                        className={`tool-marketplace-cleaner-brush-cursor ${
                          brushMode === 'restore'
                            ? 'tool-marketplace-cleaner-brush-cursor-restore'
                            : ''
                        }`}
                        style={{
                          left: `${brushCursor.x * previewScaleX}px`,
                          top: `${brushCursor.y * previewScaleY}px`,
                          width: `${Math.max(12, brushSize * previewScaleX)}px`,
                          height: `${Math.max(12, brushSize * previewScaleY)}px`,
                        }}
                      />
                    ) : null}
                    <div className="tool-product-prep-guides" aria-hidden="true">
                      <div
                        className="tool-product-prep-guide-safe-area"
                        style={{ inset: safeInsetLabel }}
                      />
                      <div className="tool-product-prep-guide-line" />
                      <span className="tool-product-prep-guide-label">Keep product inside</span>
                    </div>
                  </div>
                ) : (
                  <div className="tool-editor-empty tool-product-prep-empty tool-marketplace-cleaner-empty">
                    <strong>Preview will appear here</strong>
                    <span>
                      Upload a product photo, clean the background, then switch to Framing for the
                      final crop.
                    </span>
                    <small>
                      Local browser tool for cleanup, framing, and listing-ready export.
                    </small>
                  </div>
                )}
              </div>
            </div>

            <div className="tool-marketplace-cleaner-editor-note">
              <strong>{workflowStage === 'cleanup' ? 'Step 1: cleanup' : 'Step 2: framing'}</strong>
              <span>
                {workflowStage === 'cleanup'
                  ? brushMode === 'restore'
                    ? 'Paint to bring back parts of the original photo.'
                    : 'Start with Auto cleanup or brush away rough background areas.'
                  : 'Drag the product into place and keep it inside the safe frame.'}
              </span>
            </div>

            {workflowStage === 'cleanup' ? (
              <div className="tool-marketplace-cleaner-auto-note">
                <strong>{autoCleanupStatus.message}</strong>
                <span>
                  {autoCleanupStatus.detail ??
                    'Auto cleanup is in beta and may work best on simple product photos with a clear subject.'}
                </span>
                <small>
                  {autoCleanupStatus.acceleration === 'webgpu'
                    ? autoCleanupStatus.isFallback
                      ? 'Fallback mode is active after a device acceleration issue.'
                      : 'Using device acceleration when available.'
                    : autoCleanupStatus.acceleration === 'wasm'
                      ? 'Fallback mode is active because advanced acceleration is unavailable on this device.'
                      : 'Nothing is uploaded to a server. Use manual cleanup to refine edges after auto cleanup.'}
                </small>
                {autoCleanupStatus.phase === 'error' || autoCleanupStatus.phase === 'cancelled' ? (
                  <div className="tool-marketplace-cleaner-auto-actions">
                    <button
                      className="button button-ghost"
                      disabled={!loadedImage}
                      onClick={runAutoCleanup}
                      type="button"
                    >
                      Retry auto cleanup
                    </button>
                    <button
                      className="button button-ghost"
                      disabled={!loadedImage}
                      onClick={() => switchToManualCleanup('erase')}
                      type="button"
                    >
                      Continue manually
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {workflowStage === 'cleanup' ? (
              <div className="tool-live-controls">
                <label className="field">
                  <span>Background</span>
                  <select
                    onChange={(event) => {
                      markWorkflowCustom();
                      setBackgroundTone(event.target.value as BackgroundTone);
                      resetOutputState();
                    }}
                    value={backgroundTone}
                  >
                    {backgroundTones.map((tone) => (
                      <option
                        key={tone.value}
                        value={tone.value}
                        disabled={tone.value === 'transparent' && !canUseTransparentBackground}
                      >
                        {tone.label}
                      </option>
                    ))}
                  </select>
                  <small>
                    {canUseTransparentBackground
                      ? 'Transparent works with PNG and WebP.'
                      : 'JPG does not support transparency, so a fill color is required.'}
                  </small>
                </label>
                <label className="field tool-live-quality">
                  <span>Brush size</span>
                  <input
                    disabled={!loadedImage}
                    max="140"
                    min="12"
                    onChange={(event) => {
                      setBrushSize(Number(event.target.value));
                      resetOutputState();
                    }}
                    step="1"
                    type="range"
                    value={brushSize}
                  />
                  <small>{loadedImage ? `${brushSize}px brush` : 'Upload first'}</small>
                </label>
              </div>
            ) : (
              <div className="tool-live-controls">
                <label className="field">
                  <span>Preset</span>
                  <select
                    onChange={(event) => {
                      markWorkflowCustom();
                      const nextPresetId = event.target.value as CanvasPresetId;
                      const nextPreset =
                        canvasPresets.find((preset) => preset.id === nextPresetId) ?? activePreset;
                      setActivePresetId(nextPresetId);
                      resetOutputState();
                      resetCleanupState();

                      if (!loadedImage) {
                        return;
                      }

                      const placement = getDefaultPlacement(
                        loadedImage.width,
                        loadedImage.height,
                        nextPreset,
                      );
                      setLoadedImage({
                        ...loadedImage,
                        zoom: placement.zoom,
                        offsetX: placement.offsetX,
                        offsetY: placement.offsetY,
                      });
                    }}
                    value={activePresetId}
                  >
                    {canvasPresets.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {`${preset.label} • ${preset.width} × ${preset.height}`}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field tool-live-quality">
                  <span>Zoom</span>
                  <input
                    disabled={!loadedImage}
                    max="2.6"
                    min="0.72"
                    onChange={(event) => updateZoom(Number(event.target.value))}
                    step="0.01"
                    type="range"
                    value={loadedImage?.zoom ?? 1}
                  />
                  <small>{loadedImage ? `${loadedImage.zoom.toFixed(2)}×` : 'Upload first'}</small>
                </label>
              </div>
            )}

            <div className="tool-product-prep-advanced">
              <button
                className="button button-ghost tool-product-prep-advanced-toggle"
                onClick={() => setIsAdvancedOpen((current) => !current)}
                type="button"
              >
                {isAdvancedOpen ? 'Hide export settings' : 'Export settings'}
              </button>

              {isAdvancedOpen ? (
                <div className="tool-product-prep-advanced-panel">
                  <div className="tool-live-controls">
                    <label className="field">
                      <span>Format</span>
                      <select
                        onChange={(event) => {
                          markWorkflowCustom();
                          const nextFormat = event.target.value as OutputFormat;
                          setOutputFormat(nextFormat);
                          setBackgroundTone((current) =>
                            normalizeBackgroundForFormat(current, nextFormat),
                          );
                          resetOutputState();
                        }}
                        value={outputFormat}
                      >
                        {outputOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label
                      className={`field tool-live-quality ${
                        selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'
                      }`}
                    >
                      <span>Compression</span>
                      <input
                        disabled={!selectedOutput.qualityEnabled}
                        max="0.98"
                        min="0.45"
                        onChange={(event) => {
                          markWorkflowCustom();
                          setQuality(Number(event.target.value));
                          resetOutputState();
                        }}
                        step="0.01"
                        type="range"
                        value={quality}
                      />
                      <small>
                        {selectedOutput.qualityEnabled
                          ? outputFormat === 'webp'
                            ? 'Smaller file, softer detail.'
                            : 'Balanced file size and detail.'
                          : 'PNG keeps full quality.'}
                      </small>
                    </label>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Export</strong>
              <span>Download the finished listing image when cleanup and framing look right.</span>
            </div>
            <div className="hero-actions tool-live-actions">
              <button
                className="button"
                disabled={!loadedImage || isExporting || isLoadingImage}
                onClick={() => void runExport()}
                type="button"
              >
                {!loadedImage
                  ? 'Upload image to export'
                  : isLoadingImage
                    ? 'Preparing image…'
                    : isExporting
                      ? 'Exporting locally…'
                      : `Export as ${selectedOutput.label}`}
              </button>
            </div>
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel tool-product-prep-summary">
          <h3>Summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Canvas</strong>
              <span>{activePreset.label}</span>
            </div>
            <div>
              <strong>Background</strong>
              <span>{activeBackground.label}</span>
            </div>
            <div>
              <strong>Format</strong>
              <span>{selectedOutput.label}</span>
            </div>
            <div>
              <strong>Export size</strong>
              <span>{`${activePreset.width} × ${activePreset.height}`}</span>
            </div>
            <div>
              <strong>Safe frame</strong>
              <span>Keep the subject inside the inner guide</span>
            </div>
            <div>
              <strong>Est. weight</strong>
              <span>{resultSize ? formatFileSize(resultSize) : estimatedOutputWeight}</span>
            </div>
          </div>
          <div className="tool-live-panel-note">
            <strong>Local only</strong>
            <span>
              Cleanup, framing, and export stay in the browser. Saved files are rebuilt from canvas,
              so common metadata is stripped on save.
            </span>
          </div>
        </aside>
      </div>
    </article>
  );
}

export default function BrowserProductPhotoBackgroundCleaner(
  props: BrowserProductPhotoBackgroundCleanerProps,
) {
  return (
    <ToolRenderErrorBoundary>
      <BrowserProductPhotoBackgroundCleanerInner {...props} />
    </ToolRenderErrorBoundary>
  );
}
